import { apiBlog } from "@/api/blog.api.ts";
import { AddComment } from "@/components/comments/AddComment.tsx";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiActionPayload } from "@/types/action.types.ts";
import { ApiEntityTypes, ApiFile } from "@/types/common.types.ts";
import { useEffect, useRef } from "react";

interface BlogAddCommentProps {
  postId: number;
  save: (payload: ApiActionPayload) => Promise<boolean>;
  initialFiles?: ApiFile[];
  initValue?: string;
  onSaved?: () => void;
  onCancel?: () => void;
  label?: string;
  submitLabel?: string;
}

export function BlogAddComment({
  postId,
  save,
  initialFiles = [],
  initValue,
  onSaved,
  onCancel,
  label,
  submitLabel,
}: BlogAddCommentProps) {
  const { handleErrors } = useErrorHandle();
  const temporaryFileUuidsRef = useRef<Set<string>>(new Set());

  const deleteTemporaryFiles = async () => {
    const fileUuids = [...temporaryFileUuidsRef.current];
    temporaryFileUuidsRef.current.clear();
    await Promise.all(fileUuids.map((fileUuid) => apiBlog.deleteAttachment(fileUuid)));
  };

  useEffect(
    () => () => {
      temporaryFileUuidsRef.current.forEach((fileUuid) => {
        void apiBlog.deleteAttachment(fileUuid);
      });
    },
    []
  );

  const openFile = async (file: ApiFile) => {
    const { data, error } = await apiBlog.getAttachmentLink(postId, file.uuid);
    if (!data?.download_url) {
      handleErrors(error);
      return;
    }
    window.open(data.download_url, "_blank", "noopener,noreferrer")?.focus();
  };

  return (
    <AddComment
      entityId={postId}
      entityType={ApiEntityTypes.blog_post}
      save={save}
      withFiles
      isCollapsedOnInit={false}
      label={label}
      submitLabel={submitLabel}
      hideControlsIfEmptyText
      showControlsWhenEmpty
      initValue={initValue}
      initialFiles={initialFiles}
      onSaved={onSaved}
      onCancel={onCancel}
      onFileUploaded={(file) => temporaryFileUuidsRef.current.add(file.uuid)}
      onFileRemove={async (file) => {
        if (!temporaryFileUuidsRef.current.has(file.uuid)) return true;
        const { error } = await apiBlog.deleteAttachment(file.uuid);
        if (error) {
          handleErrors(error);
          return false;
        }
        temporaryFileUuidsRef.current.delete(file.uuid);
        return true;
      }}
      onFileOpen={openFile}
      isFileOpenDisabled={(file) =>
        !initialFiles.some((initialFile) => initialFile.uuid === file.uuid)
      }
      onFilesSaved={async () => {
        temporaryFileUuidsRef.current.clear();
      }}
      onFilesCancel={deleteTemporaryFiles}
    />
  );
}
